import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Radio, Wifi, WifiOff, HelpCircle, ArrowRight, Loader2 } from 'lucide-react';
import { apiClient } from '../../utils/apiClient';
import { useAuth } from '../../contexts/AuthContext';

const POLL_MS = 45000;

function Stat({ tone, icon: Icon, label, value }) {
    const tones = {
        green: {
            num: 'text-emerald-500 dark:text-emerald-300',
            box: 'bg-emerald-500/10 border-emerald-500/20',
            icon: 'text-emerald-500',
            pulse: true
        },
        red: {
            num: 'text-rose-500 dark:text-rose-300',
            box: 'bg-rose-500/10 border-rose-500/20',
            icon: 'text-rose-500',
            pulse: value > 0
        },
        amber: {
            num: 'text-amber-500 dark:text-amber-300',
            box: 'bg-amber-500/10 border-amber-500/20',
            icon: 'text-amber-500',
            pulse: false
        }
    };
    const t = tones[tone];

    return (
        <div className={`h-full min-w-0 flex items-center gap-3 rounded-xl border px-3 py-2 ${t.box}`}>
            <div className="relative shrink-0">
                <Icon className={`w-5 h-5 ${t.icon}`} />
                <span className={`absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full ${t.pulse ? 'animate-pulse bg-current' : 'bg-current/40'} ${t.icon}`} />
            </div>
            <div className="min-w-0">
                <motion.p
                    key={`${label}-${value}`}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`text-2xl font-black leading-none tabular-nums ${t.num}`}
                >
                    {value}
                </motion.p>
                <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 truncate">
                    {label}
                </p>
            </div>
        </div>
    );
}

export default function DeviceReachabilityCard({ onNavigate, isEditMode }) {
    const { permissions, roles, isAuthenticated } = useAuth();
    const canView = useMemo(
        () =>
            (roles || []).includes('Admin') ||
            (permissions || []).includes('devices:view_list') ||
            (permissions || []).includes('settings:manage'),
        [permissions, roles]
    );

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        if (!canView) return;
        try {
            const snap = await apiClient('DeviceReachability');
            setData(snap);
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [canView]);

    useEffect(() => {
        if (!isAuthenticated || !canView) {
            setLoading(false);
            return undefined;
        }
        load();
        const id = setInterval(() => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
            load();
        }, POLL_MS);
        return () => clearInterval(id);
    }, [isAuthenticated, canView, load]);

    const openPage = () => {
        if (isEditMode) return;
        if (typeof onNavigate === 'function') {
            onNavigate('device_reachability');
            return;
        }
        localStorage.setItem('currentPage', 'device_reachability');
        window.location.reload();
    };

    if (!canView) return null;

    if (loading && !data) {
        return (
            <div className="h-full w-full bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 shadow-md px-4 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
                <span className="text-xs text-gray-500 dark:text-gray-400">Cihaz erişimleri yükleniyor...</span>
            </div>
        );
    }

    const reachable = data?.reachableCount ?? 0;
    const unreachable = data?.unreachableCount ?? 0;
    const unknown = data?.unknownCount ?? 0;
    const total = Math.max(reachable + unreachable + unknown, 0);
    const alert = unreachable > 0;
    const share = (count) => (total > 0 ? `${Math.max((count / total) * 100, count > 0 ? 8 : 0)}%` : '0%');

    return (
        <button
            type="button"
            onClick={openPage}
            className="group relative w-full h-full overflow-hidden rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 shadow-md text-left hover:shadow-lg transition-shadow"
        >
            <div className={`absolute inset-0 ${alert ? 'bg-rose-500/5' : 'bg-emerald-500/5'}`} />
            <div className="pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-white/15 dark:via-white/10 to-transparent animate-reach-scan" />

            <div className="relative h-full grid grid-cols-2 md:grid-cols-4 gap-3 px-3 py-2.5">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="relative shrink-0 p-2 rounded-xl bg-slate-100 dark:bg-white/5">
                        <span className={`absolute inset-0 rounded-xl ${alert ? 'bg-rose-400/20' : 'bg-emerald-400/20'} animate-reach-radar`} />
                        <Radio className={`relative w-5 h-5 ${alert ? 'text-rose-400' : 'text-emerald-400'}`} />
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-bold text-gray-900 dark:text-white truncate">Cihaz Erişimleri</p>
                        <p className="mt-0.5 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                            <span className={`h-1.5 w-1.5 rounded-full ${alert ? 'bg-rose-400 animate-pulse' : 'bg-emerald-400 animate-pulse'}`} />
                            {alert ? 'Kopuk cihaz var' : 'Canlı izleme'}
                        </p>
                    </div>
                </div>

                <Stat tone="green" icon={Wifi} label="Erişilen" value={reachable} />
                <Stat tone="red" icon={WifiOff} label="Erişilemeyen" value={unreachable} />

                {unknown > 0 ? (
                    <Stat tone="amber" icon={HelpCircle} label="Belirsiz" value={unknown} />
                ) : (
                    <div className="h-full min-w-0 flex items-center justify-between gap-3 rounded-xl border border-gray-200 dark:border-white/10 px-3 py-2">
                        <div className="min-w-0 flex-1">
                            <div className="h-1.5 rounded-full bg-gray-200 dark:bg-white/10 overflow-hidden flex">
                                <span className="h-full bg-emerald-500 transition-all duration-500" style={{ width: share(reachable) }} />
                                <span className="h-full bg-rose-500 transition-all duration-500" style={{ width: share(unreachable) }} />
                            </div>
                            <p className="mt-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                Dağılım
                            </p>
                        </div>
                        <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-gray-500 dark:text-gray-400 group-hover:text-emerald-500">
                            Detay <ArrowRight className="w-3.5 h-3.5" />
                        </span>
                    </div>
                )}
            </div>
        </button>
    );
}
